import { api, esquecerInscricaoNaApi } from './api';

/**
 * O push no navegador — onda 548 (Plano 2 · Comercial, Etapa 1, Tarefa 4).
 *
 * `inscreverNoPush()` pede a permissão de notificação, inscreve o navegador
 * no serviço de push dele (com a chave pública VAPID do servidor) e manda a
 * inscrição à API (`POST /avisos/push`). Quem mostra a notificação é o
 * `sw.js` (`push` e `notificationclick`).
 *
 * ─── O iPhone ─────────────────────────────────────────────────────────
 *
 * No iPhone e no iPad, a notificação da web só existe a partir do iOS 16.4
 * e só para o site adicionado à tela de início, aberto por lá. No Safari
 * comum não há `PushManager`, e pedir a permissão não faz nada. Por isso o
 * iPhone fora da tela de início tem resposta própria
 * (`iphone_sem_tela_de_inicio`): a tela mostra o passo a passo em vez de um
 * botão que não funciona.
 *
 * ─── O navegador é de quem está nele (revisão da onda 548, I-3 e M-3) ──
 *
 *  · `reafirmarInscricao()`: a moldura a chama a cada pessoa que entra
 *    (o sino, por `usuarioId`, também no «Entrar como» e no «Voltar a ser
 *    eu»). Se o navegador já tem inscrição e a permissão está dada, ela vai
 *    de novo à API em nome de quem está agora — a de quem usou o navegador
 *    antes passa a esta pessoa. Nunca pede permissão nem inscreve do zero.
 *  · A chave do servidor mudou (o par VAPID foi gerado de novo): a inscrição
 *    feita com a chave velha não recebe mais nada. A tela a desfaz e faz
 *    outra com a chave nova, sem perguntar (a permissão já foi dada).
 *  · `esquecerEsteAparelho(token)`: o «Sair» desfaz a inscrição na API (com
 *    o token de quem sai) e no navegador, para as notificações de quem saiu
 *    não chegarem a quem entrar depois.
 */

export type ResultadoDoPush = 'ok' | 'negado' | 'sem_suporte' | 'iphone_sem_tela_de_inicio';

/** iPhone, iPod ou iPad (o iPad novo se diz Mac, mas tem toque). */
export function ehIphone(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua)
    || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);
}

/** Aberto pela tela de início (o aplicativo instalado), e não pela aba do navegador. */
export function naTelaDeInicio(): boolean {
  const n = navigator as Navigator & { standalone?: boolean };
  return n.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/**
 * O que impede o push neste aparelho, sem perguntar nada a ninguém — a tela
 * usa para decidir o que mostrar antes do toque. `null`: dá para tentar.
 */
export function impedimentoDoPush(): Exclude<ResultadoDoPush, 'ok'> | null {
  if (ehIphone() && !naTelaDeInicio()) return 'iphone_sem_tela_de_inicio';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'sem_suporte';
  }
  if (Notification.permission === 'denied') return 'negado';
  return null;
}

/** Bytes → base64url, sem o preenchimento (como a chave VAPID vem do servidor). */
function base64url(buf: ArrayBuffer): string {
  let bin = '';
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** A inscrição foi feita com esta chave do servidor? */
function daChave(inscricao: PushSubscription, chave: string): boolean {
  const k = inscricao.options?.applicationServerKey;
  return !!k && base64url(k) === chave.replace(/=+$/, '');
}

/** A chave do servidor vem em base64url; o navegador a quer em bytes. */
function bytesDaChave(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** A chave pública do servidor; `null` quando o push está desligado lá. */
export async function chaveDoServidor(): Promise<string | null> {
  const r = await api<{ chave: string | null }>('/avisos/push/chave');
  return r?.chave ?? null;
}

/** A inscrição deste navegador, se houver. */
export async function inscricaoAtual(): Promise<PushSubscription | null> {
  if (impedimentoDoPush() === 'sem_suporte' || impedimentoDoPush() === 'iphone_sem_tela_de_inicio') return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function inscreverNoPush(): Promise<ResultadoDoPush> {
  const impedimento = impedimentoDoPush();
  if (impedimento === 'iphone_sem_tela_de_inicio' || impedimento === 'sem_suporte') return impedimento;

  const chave = await chaveDoServidor();
  if (!chave) return 'sem_suporte';

  const permissao = await Notification.requestPermission();
  if (permissao !== 'granted') return 'negado';

  const reg = await navigator.serviceWorker.ready;
  await inscricaoComAChave(reg, chave, true);
  return 'ok';
}

/**
 * A inscrição deste navegador com a chave do servidor, levada à API em nome
 * de quem está na sessão. A inscrição com outra chave é desfeita e refeita;
 * sem inscrição, só cria quando `criar` (o toque em «Receber neste
 * aparelho»).
 */
async function inscricaoComAChave(reg: ServiceWorkerRegistration, chave: string, criar: boolean):
  Promise<'nada' | 'reafirmada' | 'renovada'> {
  let inscricao = await reg.pushManager.getSubscription();
  let renovada = false;
  if (inscricao && !daChave(inscricao, chave)) {
    const velha = inscricao.endpoint;
    await inscricao.unsubscribe().catch(() => false);
    await api('/avisos/push', { metodo: 'DELETE', corpo: { endpoint: velha } }).catch(() => undefined);
    inscricao = null;
    renovada = true;
  }
  if (!inscricao) {
    if (!criar && !renovada) return 'nada';
    inscricao = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: bytesDaChave(chave),
    });
  }
  await api('/avisos/push', { metodo: 'POST', corpo: inscricao.toJSON() });
  return renovada ? 'renovada' : 'reafirmada';
}

/**
 * A inscrição que este navegador já tem passa a quem está na sessão (e
 * ganha a chave nova, se a do servidor mudou). Sem permissão dada ou sem
 * inscrição, não faz nada — nunca pergunta.
 */
export async function reafirmarInscricao(): Promise<'nada' | 'reafirmada' | 'renovada'> {
  if (impedimentoDoPush() !== null || Notification.permission !== 'granted') return 'nada';
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg || !(await reg.pushManager.getSubscription())) return 'nada';
  const chave = await chaveDoServidor();
  if (!chave) return 'nada';
  return inscricaoComAChave(reg, chave, false);
}

/**
 * O «Sair»: a inscrição deste navegador sai da API (com o token de quem
 * sai, recebido antes de ele ser apagado) e do navegador. Não segura a
 * saída: roda por trás, e qualquer falha só deixa de desfazer — a inscrição
 * desfeita no navegador já não recebe nada, e o serviço de push responde
 * 410 ao próximo envio, o que a desliga na API também.
 *
 * O pedido à API mora no `api.ts` (`esquecerInscricaoNaApi`): o token entra
 * em um lugar só (54.3).
 */
export async function esquecerEsteAparelho(tokenDeQuemSai: string | null): Promise<void> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const inscricao = await reg?.pushManager.getSubscription();
    if (!inscricao) return;
    const endpoint = inscricao.endpoint;
    if (tokenDeQuemSai) {
      await esquecerInscricaoNaApi(endpoint, tokenDeQuemSai);
    }
    await inscricao.unsubscribe().catch(() => false);
  } catch { /* a saída não espera pelo push */ }
}

/** Para de receber neste navegador: desfaz a inscrição aqui e avisa a API. */
export async function cancelarPush(): Promise<void> {
  const inscricao = await inscricaoAtual();
  if (!inscricao) return;
  const endpoint = inscricao.endpoint;
  await inscricao.unsubscribe().catch(() => false);
  await api('/avisos/push', { metodo: 'DELETE', corpo: { endpoint } });
}
